<template>
    <span
        class="betBuilderCombinationWorker"
        aria-hidden="true"
    />
</template>

<script lang="ts" setup>
    import { computed, ref, toRefs, watch } from 'vue';
    import { BetBuilderCombinationsContext } from '@/components/recommendations/BetBuilderCombinationsContext';
    import type {
        IBetBuilderCombination,
        IBetBuilderCombinationSelection,
        IBetBuilderRecommendation,
    } from '@/interface/IBetBuilderRecommendation';
    import type { IBetBuilderMarket } from '@/interface/betBuilder';
    import { BetBuilderSelectionStatus } from '@/interface/enum';
    import { useBetBuilderMatch } from '@/libraries/oddsApi/composables/useBetBuilderMatch';
    import { useRecommendationEventQuery } from '@/libraries/oddsApi/composables/useRecommendationEventQuery';
    import { useBetBuilderConnection } from '@/libraries/recommendation/useBetBuilderConnection';

    interface Props {
        combination: IBetBuilderRecommendation;
        isEnabled: boolean;
    }

    const props = defineProps<Props>();

    const { isEnabled, combination } = toRefs(props);

    const combinationKey = computed(() => combination.value.combinationKey);

    const {
        removeBetBuilderCombination,
        updateBetBuilderCombinationLoaded,
        setBetBuilderCombinationValid,
        setCombinationWithEvent,
    } = BetBuilderCombinationsContext.inject();

    const realtimeMaxOdds = ref(combination.value.maxOdds);
    const hasReceivedRealtimeUpdate = ref(false);

    watch(
        () => combination.value.maxOdds,
        (next) => {
            if (!hasReceivedRealtimeUpdate.value) {
                realtimeMaxOdds.value = next;
            }
        },
    );

    useBetBuilderConnection(
        combination.value.combinationKey,
        (payload) => {
            hasReceivedRealtimeUpdate.value = true;
            realtimeMaxOdds.value = payload.maxOdds;
        },
        () => {
            removeBetBuilderCombination(combination.value.combinationKey);
        },
        isEnabled,
    );

    function findMarketAndSelection(markets: IBetBuilderMarket[], selectionId: number): IBetBuilderCombinationSelection | null {
        const market = markets.find(m => m.selections.some(s => s.id === selectionId));
        if (!market) {
            return null;
        }

        const selection = market.selections.find(s => s.id === selectionId);
        return selection ? { market, selection } : null;
    }

    const eventId = computed(() => combination.value.eventId);
    const { event, loaded: eventLoaded } = useRecommendationEventQuery(eventId, false);

    const { betBuilderMatch, loaded: betBuilderMatchLoaded } = useBetBuilderMatch(eventId);

    const selections = computed(() => combination.value.selectionIds
        .map(selectionId => findMarketAndSelection(betBuilderMatch.value?.markets ?? [], selectionId))
        .filter((s): s is IBetBuilderCombinationSelection => s !== null));

    const isLoaded = computed(() => eventLoaded.value && betBuilderMatchLoaded.value);

    const isValid = computed(() => event.value !== null
        && betBuilderMatch.value !== null
        && selections.value.length === combination.value.selectionIds.length
        && selections.value.every(s => s.selection.status !== BetBuilderSelectionStatus.Closed));

    const combinationWithEvent = computed<IBetBuilderCombination>(() => ({
        event: event.value,
        selections: selections.value,
        maxOdds: realtimeMaxOdds.value,
        isValid: isValid.value,
    }));

    watch(isLoaded, (loaded) => {
        if (!loaded) return;
        updateBetBuilderCombinationLoaded(combinationKey.value);
    });

    watch(isValid, (valid) => {
        setBetBuilderCombinationValid(combinationKey.value, valid);
    });

    watch(
        combinationWithEvent,
        (payload) => {
            setCombinationWithEvent(combinationKey.value, payload);
        },
        { deep: true, immediate: true },
    );
</script>

<style lang="scss" scoped>
.betBuilderCombinationWorker {
    display: none;
}
</style>
